import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import {
    AlertCircle,
    FileText,
    RefreshCw,
} from 'lucide-react';
import reportService from '../../services/reportService';
import cashBoxService, { CashBox } from '../../services/cashBoxService';
import { ReportMovementDTO } from '../../types';
import InlineDatePicker from '../InlineDatePicker';
import { showError } from '../Alerts';

interface CashBoxMovementsModalProps {
    isOpen: boolean;
    onClose: () => void;
    cashBoxId: number | null;
}

export default function CashBoxMovementsModal({ isOpen, onClose, cashBoxId }: CashBoxMovementsModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
    const [movementsData, setMovementsData] = useState<ReportMovementDTO[]>([]);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        if (!isOpen || cashBoxId === null) return;
        const load = async () => {
            try {
                const boxes = await cashBoxService.getAllCashBoxes();
                setCashBoxes(boxes);
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                setStartDate(firstDay.toISOString().split('T')[0]);
                setEndDate(lastDay.toISOString().split('T')[0]);
            } catch {
                showError('Error al cargar la información de la caja');
            }
        };
        load();
    }, [isOpen, cashBoxId]);

    useEffect(() => {
        if (!isOpen) { setMovementsData([]); setError(null); }
    }, [isOpen]);

    useEffect(() => {
        if (!cashBoxId || !startDate || !endDate) return;
        const fetch = async () => {
            setError(null);
            setLoading(true);
            try {
                const data = await reportService.getCashBoxMovements({ cashBoxId, startDate, endDate });
                setMovementsData(data);
            } catch (err: any) {
                const msg = err.response?.data?.message || 'Error al cargar los movimientos.';
                setError(msg);
                showError(msg);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cashBoxId, startDate, endDate]);

    const selectedBox = cashBoxes.find((b) => b.id === cashBoxId);
    const currency = selectedBox?.currency || 'ARS';
    const currencySymbol = currency === 'USD' ? 'US$' : '$';

    const fmt = (amount: number) =>
        `${currencySymbol} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const fmtTime = (d: string) =>
        new Date(d).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

    return (
        <>
            <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
                <DialogContent className="w-[95vw] max-w-4xl bg-card border-border p-0 max-h-[90vh] flex flex-col">

                    {/* Header */}
                    <DialogHeader className="shrink-0 px-5 pt-5 pb-4 border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                <FileText className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <DialogTitle className="text-lg font-bold text-foreground">
                                    Movimientos Detallados
                                </DialogTitle>
                                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                                    {selectedBox?.name || 'Cargando...'}
                                    {currency && (
                                        <Badge variant="outline" className="px-1.5 py-0 h-5 text-[10px]">{currency}</Badge>
                                    )}
                                </p>
                            </div>
                        </div>

                    </DialogHeader>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                        {/* Date filters */}
                        <div className="grid grid-cols-2 gap-3">
                            <InlineDatePicker
                                id="movements-desde"
                                label="Desde"
                                value={startDate}
                                onChange={setStartDate}
                            />
                            <InlineDatePicker
                                id="movements-hasta"
                                label="Hasta"
                                value={endDate}
                                onChange={setEndDate}
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-3 p-4 text-sm rounded-xl bg-destructive/5 border border-destructive/20 text-destructive">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-24">
                                <RefreshCw className="h-8 w-8 animate-spin text-primary mb-4" />
                                <p className="text-sm text-muted-foreground">Cargando movimientos...</p>
                            </div>
                        ) : movementsData.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center border border-border rounded-xl">
                                <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
                                    <FileText className="h-8 w-8 text-muted-foreground/40" />
                                </div>
                                <p className="text-base font-semibold text-foreground">Sin movimientos en este período</p>
                                <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                                    No hay registros para el rango de fechas seleccionado.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-muted-foreground">
                                        <span className="font-semibold text-foreground">{movementsData.length}</span> movimiento{movementsData.length !== 1 ? 's' : ''} encontrado{movementsData.length !== 1 ? 's' : ''}
                                    </p>
                                    <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground bg-muted/30">
                                        Visión de Auditoría
                                    </Badge>
                                </div>

                                <div className="border border-border rounded-xl overflow-hidden bg-card">
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-muted/20">
                                                    <TableHead className="py-3">Fecha</TableHead>
                                                    <TableHead className="py-3">Descripción</TableHead>
                                                    <TableHead className="py-3">Tipo</TableHead>
                                                    <TableHead className="py-3 text-right">Monto</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {movementsData.map((mov) => {
                                                    const isCredito = mov.entryType === 'CREDITO';
                                                    const sign = isCredito ? '+' : '-';
                                                    const colorClass = isCredito ? 'text-success' : 'text-destructive';
                                                    const parsedDate = new Date(mov.date);
                                                    const showTime = mov.date.includes('T') && !mov.date.endsWith('T00:00:00');

                                                    return (
                                                        <TableRow key={mov.id} className="hover:bg-muted/30 transition-colors">
                                                            <TableCell className="py-3 align-top whitespace-nowrap text-sm text-foreground">
                                                                <div>
                                                                    <p>{parsedDate.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                                                    {showTime && (
                                                                        <p className="text-[11px] text-muted-foreground mt-0.5">{fmtTime(mov.date)}</p>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="py-3 align-top">
                                                                <p className="text-sm text-foreground">{mov.description || 'Movimiento de caja'}</p>
                                                                {mov.transactionId && (
                                                                    <p className="text-[10px] text-muted-foreground uppercase mt-0.5">Ref: TXN-{mov.transactionId}</p>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="py-3 align-top whitespace-nowrap">
                                                                <Badge
                                                                    variant="outline"
                                                                    className={`text-[10px] uppercase font-medium bg-background ${
                                                                        isCredito
                                                                            ? 'border-success/30 text-success bg-success/5'
                                                                            : 'border-destructive/30 text-destructive bg-destructive/5'
                                                                    }`}
                                                                >
                                                                    {mov.transactionType || mov.entryType}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="py-3 align-top text-right">
                                                                <span className={`text-sm font-bold tabular-nums ${colorClass}`}>
                                                                    {sign}{fmt(mov.amount)}
                                                                </span>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="shrink-0 flex justify-end px-5 py-3 border-t border-border">
                        <Button variant="outline" size="sm" onClick={onClose} className="rounded-lg">
                            Cerrar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

        </>
    );
}
